import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - INDOT RSP 401-R-577
// =============================================================================

// Pay factor tiers per 0.1-mile segment (MRI in/mi)
const PAY_TIERS = [
  { max: 35, pf: 1.06 },
  { max: 40, pf: 1.05 },
  { max: 45, pf: 1.04 },
  { max: 50, pf: 1.03 },
  { max: 55, pf: 1.02 },
  { max: 60, pf: 1.01 },
  { max: 70, pf: 1.00 },
  { max: 75, pf: 0.99 },
  { max: 80, pf: 0.98 },
  { max: 85, pf: 0.96 },
  { max: 90, pf: 0.95 },
  { max: Infinity, pf: 0.95 }, // >90 for Type A, >110 for Type B
];

// Type A: Interstate or multiple opportunities (lifts)
// Type B: Non-interstate with single opportunity
const TYPES = {
  A: {
    id: 'A',
    label: 'Type A',
    description: 'Interstate / Multi-lift',
    correctionThreshold: 90,
    correctionTarget: 70,
    alrThreshold: 160,
  },
  B: {
    id: 'B',
    label: 'Type B',
    description: 'Single opportunity',
    correctionThreshold: 110,
    correctionTarget: 90,
    alrThreshold: 170,
  },
};

const DEFAULT_SETTINGS = {
  type: 'A',
  unitPrice: 95,
  laneWidth: 12,
  spreadRate: 220, // lb/sq yd
};

// =============================================================================
// CALCULATION
// =============================================================================

const getPayFactor = (mri, type) => {
  const cfg = TYPES[type];
  // For Type B, segments 90-110 get 0.95 but no correction
  // For segments above correction threshold, still 0.95 but flagged
  for (const t of PAY_TIERS) {
    if (mri <= t.max) return t.pf;
  }
  return 0.95;
};

const getPayColor = (pf) => {
  if (pf >= 1.04) return 'green';
  if (pf >= 1.01) return 'lime';
  if (pf >= 1.00) return 'blue';
  if (pf >= 0.98) return 'yellow';
  if (pf >= 0.96) return 'orange';
  return 'red';
};

const calculate = (segments, settings) => {
  const { type, unitPrice, laneWidth, spreadRate } = settings;
  const cfg = TYPES[type];
  if (!cfg || !segments?.length) return null;

  const T = 2000; // lb/ton conversion
  let totalQs = 0;
  let totalBasePay = 0;
  let flaggedCount = 0;
  let alrCount = 0;
  let requiresCorrectionCount = 0;

  // First pass: sort by station and calculate widths
  const sortedSegments = segments
    .map((seg, index) => {
      const startStation = seg.from_station_ft || 0;
      const stopStation = seg.to_station_ft || 0;
      const segmentWidth = seg.distance_ft || Math.abs(stopStation - startStation);
      return {
        ...seg,
        originalIndex: index,
        startStation,
        stopStation,
        segmentWidth,
      };
    })
    .sort((a, b) => a.startStation - b.startStation);

  // Second pass: calculate cumulative distances
  let cumulativeDistance = 0;
  const rows = sortedSegments.map((seg, i) => {
    const startDist = cumulativeDistance;
    const stopDist = cumulativeDistance + seg.segmentWidth;
    cumulativeDistance = stopDist;

    const mri = seg.iri_avg_in_per_mi || 0;
    const leftIRI = seg.iri_left_in_per_mi || seg.left_iri || 0;
    const rightIRI = seg.iri_right_in_per_mi || seg.right_iri || 0;
    const area = (seg.segmentWidth * laneWidth) / 9; // sq yd
    const pf = getPayFactor(mri, type);
    
    // qs = (PFs - 1.00) × (A × S / T × U)
    const segBasePay = (area * spreadRate / T) * unitPrice;
    const qs = (pf - 1.00) * segBasePay;
    
    // Flags
    const isALR = mri >= cfg.alrThreshold;
    const requiresCorrection = mri > cfg.correctionThreshold;
    
    if (isALR) alrCount++;
    if (requiresCorrection) requiresCorrectionCount++;
    if (mri >= 90) flaggedCount++;

    totalQs += qs;
    totalBasePay += segBasePay;

    return {
      num: i + 1,
      mri,
      leftIRI,
      rightIRI,
      startDist,
      stopDist,
      startStation: seg.startStation,
      stopStation: seg.stopStation,
      len: seg.segmentWidth,
      area,
      pf,
      qs,
      segBasePay,
      isALR,
      requiresCorrection,
    };
  });

  const avgMRI = rows.length > 0 ? rows.reduce((s, r) => s + r.mri, 0) / rows.length : 0;
  const avgPF = rows.length > 0 ? rows.reduce((s, r) => s + r.pf, 0) / rows.length : 1.0;

  return {
    rows,
    avgMRI,
    avgPF,
    totalBasePay,
    totalQs,
    adjustedPay: totalBasePay + totalQs,
    flaggedCount,
    alrCount,
    requiresCorrectionCount,
    cfg,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');
const fmtStation = (ft) => ft == null ? '—' : `${Math.floor(ft/100)}+${String(Math.round(ft%100)).padStart(2,'0')}`;
const fmtCurrency = (v) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct = (v) => `${(v * 100).toFixed(0)}%`;

// =============================================================================
// TIER BAR
// =============================================================================

const TierBar = ({ avgMRI }) => {
  const max = 120;
  const tiers = [
    { max: 35, pf: 1.06, color: 'bg-green-600' },
    { max: 40, pf: 1.05, color: 'bg-green-500' },
    { max: 45, pf: 1.04, color: 'bg-green-400' },
    { max: 50, pf: 1.03, color: 'bg-lime-500' },
    { max: 55, pf: 1.02, color: 'bg-lime-400' },
    { max: 60, pf: 1.01, color: 'bg-lime-300' },
    { max: 70, pf: 1.00, color: 'bg-blue-500' },
    { max: 75, pf: 0.99, color: 'bg-yellow-400' },
    { max: 80, pf: 0.98, color: 'bg-yellow-500' },
    { max: 85, pf: 0.96, color: 'bg-orange-400' },
    { max: 90, pf: 0.95, color: 'bg-orange-500' },
    { max: max, pf: 0.95, color: 'bg-red-500' },
  ];

  return (
    <div className="relative pt-5">
      {/* MRI Marker Label */}
      {avgMRI > 0 && avgMRI <= max && (
        <div 
          className="absolute top-0 -translate-x-1/2 text-xs font-bold bg-gray-800 text-white px-1.5 py-0.5 rounded-sm"
          style={{ left: `${(avgMRI/max)*100}%` }}
        >
          {avgMRI.toFixed(0)}
        </div>
      )}
      
      {/* Bar */}
      <div className="relative h-6 flex rounded-sm overflow-hidden border border-gray-300">
        {tiers.map((t, i) => {
          const prev = i === 0 ? 0 : tiers[i-1].max;
          const w = ((t.max - prev) / max) * 100;
          return (
            <div 
              key={i} 
              className={cn('flex items-center justify-center text-[10px] font-bold text-white', t.color)} 
              style={{ width: `${w}%` }}
              title={`${prev}-${t.max}: ${fmtPct(t.pf)}`}
            >
              {w > 8 && fmtPct(t.pf)}
            </div>
          );
        })}
        
        {/* Marker Line */}
        {avgMRI > 0 && avgMRI <= max && (
          <div className="absolute top-0 bottom-0 w-0.5 bg-gray-800" style={{ left: `${(avgMRI/max)*100}%` }} />
        )}
      </div>
      
      {/* Scale */}
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>0</span>
        <span className="text-gray-500">MRI (in/mi)</span>
        <span>{max}+</span>
      </div>
    </div>
  );
};

// =============================================================================
// PAY FACTOR MODAL
// =============================================================================

const PayFactorModal = ({ open, onClose, type }) => {
  const cfg = TYPES[type];
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-sm shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <DialogTitle className="text-base font-semibold text-gray-900">
              INDOT Pay Factor Table
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3">RSP 401-R-577 — Per 0.1-mile segment</p>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">MRI Range</th>
                  <th className="px-3 py-2 text-left">Pay Factor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {PAY_TIERS.slice(0, -1).map((t, i) => {
                  const prev = i === 0 ? 0 : PAY_TIERS[i-1].max;
                  const color = getPayColor(t.pf);
                  return (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-gray-700">{prev} – {t.max} in/mi</td>
                      <td className="px-3 py-1.5">
                        <span className={cn(
                          'inline-block px-2 py-0.5 rounded-sm text-xs font-bold',
                          color === 'green' && 'bg-green-100 text-green-700',
                          color === 'lime' && 'bg-lime-100 text-lime-700',
                          color === 'blue' && 'bg-blue-100 text-blue-700',
                          color === 'yellow' && 'bg-yellow-100 text-yellow-700',
                          color === 'orange' && 'bg-orange-100 text-orange-700',
                          color === 'red' && 'bg-red-100 text-red-700',
                        )}>
                          {fmtPct(t.pf)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-red-50">
                  <td className="px-3 py-1.5 text-red-700">&gt;{cfg?.correctionThreshold} in/mi</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-block px-2 py-0.5 rounded-sm text-xs font-bold bg-red-100 text-red-700">
                      95% + Correct to ≤{cfg?.correctionTarget}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="mt-4 p-3 bg-gray-50 rounded-sm text-xs text-gray-600 space-y-1">
              <p><strong>Type A:</strong> Interstate or multi-lift — correct &gt;90 to ≤70, ALR ≥160</p>
              <p><strong>Type B:</strong> Single opportunity — correct &gt;110 to ≤90, ALR ≥170</p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// SUMMARY CARD
// =============================================================================

const SummaryCard = ({ label, value, unit, className = '', valueClassName = '' }) => (
  <div className={cn('bg-gray-50 border border-gray-200 rounded-sm px-3 py-2', className)}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={cn('text-xl font-bold text-gray-900', valueClassName)}>
      {value}
      {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
    </div>
  </div>
);

// =============================================================================
// MAIN
// =============================================================================

export default function IndianaPayAdjustment({ project, segments }) {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...project?.payAdjustment });
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);

  const data = useMemo(() => calculate(segments, settings), [segments, settings]);
  
  const rows = useMemo(() => {
    if (!data?.rows) return [];
    let r = [...data.rows];
    if (flaggedOnly) r = r.filter(x => x.requiresCorrection || x.isALR);
    return r;
  }, [data?.rows, flaggedOnly]);

  const exportCSV = () => {
    const csv = [
      `Segment,Start Dist,Stop Dist,Start Station,Stop Station,Left IRI,Right IRI,MRI,Pay Factor,Adjustment,Requires Correction,ALR`,
      ...rows.map(r => `${r.num},${r.startDist.toFixed(1)},${r.stopDist.toFixed(1)},${fmtStation(r.startStation)},${fmtStation(r.stopStation)},${r.leftIRI.toFixed(1)},${r.rightIRI.toFixed(1)},${r.mri.toFixed(1)},${fmtPct(r.pf)},${r.qs.toFixed(2)},${r.requiresCorrection ? 'Yes' : ''},${r.isALR ? 'Yes' : ''}`)
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'in-pay-adjustment.csv';
    a.click();
  };

  const typeConfig = TYPES[settings.type];

  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      {/* Pay Factor Modal */}
      <PayFactorModal open={showPayTable} onClose={() => setShowPayTable(false)} type={settings.type} />

      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
        <h3 className="text-base font-semibold text-gray-900">INDOT Pay Adjustment</h3>
        <span className="text-xs text-gray-400">RSP 401-R-577</span>
        
        <div className="flex-1" />

        {/* Pay Table Button */}
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Factors
        </button>

        <div className="h-5 w-px bg-gray-300" />

        {/* Type */}
        <Listbox value={settings.type} onChange={(v) => setSettings(s => ({ ...s, type: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm font-medium bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500">
              {typeConfig?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute right-0 z-30 mt-1 w-56 bg-white border border-gray-200 rounded-sm shadow-lg py-1 text-sm">
              {Object.values(TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer', active && 'bg-blue-50')}>
                  <span className="font-medium">{t.label}</span>
                  <span className="text-gray-500 ml-1">— {t.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <div className="h-5 w-px bg-gray-300" />

        {/* Inline Settings */}
        {[
          { k: 'unitPrice', l: '$/ton', w: 'w-16' },
          { k: 'laneWidth', l: 'Width', w: 'w-14' },
          { k: 'spreadRate', l: 'lb/sy', w: 'w-16' },
        ].map(({ k, l, w }) => (
          <div key={k} className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">{l}</label>
            <input
              type="number"
              value={settings[k]}
              onChange={(e) => setSettings(s => ({ ...s, [k]: parseFloat(e.target.value) || 0 }))}
              className={cn('h-8 px-2 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500', w)}
            />
          </div>
        ))}

        <div className="h-5 w-px bg-gray-300" />

        <button onClick={exportCSV} className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-1.5">
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>

      {/* Summary */}
      {data ? (
        <div className="px-4 py-3 border-b border-gray-200 space-y-3">
          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-3">
            <SummaryCard label="Avg MRI" value={data.avgMRI.toFixed(1)} unit="in/mi" />
            <SummaryCard 
              label="Avg Pay Factor" 
              value={fmtPct(data.avgPF)}
              valueClassName={cn(
                data.avgPF >= 1.02 && 'text-green-600',
                data.avgPF >= 1.00 && data.avgPF < 1.02 && 'text-blue-600',
                data.avgPF >= 0.98 && data.avgPF < 1.00 && 'text-yellow-600',
                data.avgPF < 0.98 && 'text-red-600',
              )}
            />
            <SummaryCard label="Base Pay" value={fmtCurrency(data.totalBasePay)} />
            <SummaryCard label="Adjusted Pay" value={fmtCurrency(data.adjustedPay)} />
            <SummaryCard 
              label="Net Qs"
              value={`${data.totalQs >= 0 ? '+' : ''}${fmtCurrency(data.totalQs)}`}
              className={cn(
                data.totalQs > 0 && 'bg-green-50 border-green-200',
                data.totalQs < 0 && 'bg-red-50 border-red-200',
              )}
              valueClassName={cn(
                data.totalQs > 0 && 'text-green-700',
                data.totalQs < 0 && 'text-red-700',
              )}
            />
          </div>

          {/* Tier Bar */}
          <TierBar avgMRI={data.avgMRI} />

          {/* Alerts */}
          {data.requiresCorrectionCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {data.requiresCorrectionCount} segment{data.requiresCorrectionCount > 1 ? 's' : ''} &gt;{typeConfig.correctionThreshold} in/mi — must correct to ≤{typeConfig.correctionTarget}
            </div>
          )}
          {data.alrCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 rounded-sm">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {data.alrCount} ALR{data.alrCount > 1 ? 's' : ''} ≥{typeConfig.alrThreshold} in/mi — localized roughness correction required
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">No segment data</div>
      )}

      {/* Table */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-3 text-sm">
          <span className="text-gray-700 font-medium">Segments</span>
          <span className="text-gray-400">{data?.rows?.length || 0} total</span>
          {(data?.requiresCorrectionCount > 0 || data?.alrCount > 0) && (
            <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer ml-2">
              <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} className="w-3.5 h-3.5 text-blue-600 rounded-sm" />
              Issues only
            </label>
          )}
        </div>

        {rows.length > 0 ? (
          <div className="flex-1 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Start Dist</th>
                  <th className="px-4 py-2 text-left">Stop Dist</th>
                  <th className="px-4 py-2 text-left">Start Station</th>
                  <th className="px-4 py-2 text-left">Stop Station</th>
                  <th className="px-4 py-2 text-left">Left IRI</th>
                  <th className="px-4 py-2 text-left">Right IRI</th>
                  <th className="px-4 py-2 text-left">MRI</th>
                  <th className="px-4 py-2 text-left">Pay Factor</th>
                  <th className="px-4 py-2 text-left border-l border-gray-200">Qs</th>
                  <th className="px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const color = getPayColor(r.pf);
                  const hasIssue = r.requiresCorrection || r.isALR;
                  return (
                    <tr key={r.num} className={cn('hover:bg-gray-50', hasIssue && 'bg-red-50')}>
                      <td className="px-4 py-2 font-medium text-gray-900">{r.num}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{r.startDist.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{r.stopDist.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{fmtStation(r.startStation)}</td>
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{fmtStation(r.stopStation)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.leftIRI.toFixed(1)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.rightIRI.toFixed(1)}</td>
                      <td className={cn('px-4 py-2 font-semibold', hasIssue ? 'text-red-600' : 'text-gray-900')}>{r.mri.toFixed(1)}</td>
                      <td className="px-4 py-2">
                        <span className={cn(
                          'inline-block px-2 py-0.5 rounded-sm text-xs font-bold',
                          color === 'green' && 'bg-green-100 text-green-700',
                          color === 'lime' && 'bg-lime-100 text-lime-700',
                          color === 'blue' && 'bg-blue-100 text-blue-700',
                          color === 'yellow' && 'bg-yellow-100 text-yellow-700',
                          color === 'orange' && 'bg-orange-100 text-orange-700',
                          color === 'red' && 'bg-red-100 text-red-700',
                        )}>
                          {fmtPct(r.pf)}
                        </span>
                      </td>
                      <td className={cn('px-4 py-2 font-mono text-xs border-l border-gray-200', r.qs >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {r.qs >= 0 ? '+' : ''}{fmtCurrency(r.qs)}
                      </td>
                      <td className="px-4 py-2">
                        {hasIssue && <ExclamationTriangleIcon className="h-4 w-4 text-red-500 inline" title={r.requiresCorrection ? 'Requires Correction' : 'ALR'} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <XCircleIcon className="h-10 w-10 mx-auto mb-2" />
              <p>{flaggedOnly ? 'No flagged segments' : 'No segments'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}